// =====================================================================
// FichePersonne — la fiche d'un membre de la famille
// =====================================================================
// Deux usages, dont un essentiel :
//
//   compléter un nœud ANONYME. Quand on déclare un grand-père sans
//   nommer le parent par lequel on y est rattaché, ou une demi-fratrie
//   dont le parent commun n'est pas nommé, Yambi crée un nœud sans
//   identité, qui n'existe que pour porter le lien. C'est ici qu'on lui
//   donne enfin un nom — et tout le sous-arbre se résout d'un coup.
//
// `completer_personne` ne remplit que les champs vides : on ne peut pas
// écraser une information posée par un autre membre de la famille.
//
// C'est aussi d'ici qu'on CONTESTE un lien. Quand la personne ouverte est
// un parent direct ou un enfant direct, le lien qui nous relie est le
// nôtre : on peut dire qu'il est faux, et il est retiré. Le bouton
// n'apparaît pas ailleurs — un lien entre deux tiers ne nous appartient
// pas.
// =====================================================================

import { useState } from 'react';
import { api, ErreurApi } from '../api';
import type { Identite, Moi, Parent } from '../../partage/types';
import { Alerte, Champ, IconeLieu, IconePersonne, nomComplet } from './base';
import { Avatar, trancheDepuisGeneration } from './Avatar';
import { Recits } from './Recits';

export function FichePersonne({
  personne,
  moi,
  onFerme,
  onModifie,
}: {
  personne: Parent;
  moi: Moi;
  onFerme: () => void;
  onModifie: () => void;
}) {
  const sansNom = !personne.nom && !personne.prenoms;

  const [edition, setEdition] = useState(sansNom);
  const [identite, setIdentite] = useState<Identite>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [confirmeSuppression, setConfirmeSuppression] = useState(false);
  const [contestation, setContestation] = useState(false);
  const [motif, setMotif] = useState('');

  // Le lien direct qui nous relie, s'il existe. dx = ma distance à
  // l'ancêtre commun, dy la sienne : un parent, c'est l'ancêtre lui-même
  // (1, 0) ; un enfant, c'est moi l'ancêtre (0, 1).
  const estMonParent =
    personne.profondeur_moi === 1 && personne.profondeur_autre === 0;
  const estMonEnfant =
    personne.profondeur_moi === 0 && personne.profondeur_autre === 1;

  const lienDirect = estMonParent
    ? { enfant: moi.personne_id, parent: personne.personne_id }
    : estMonEnfant
      ? { enfant: personne.personne_id, parent: moi.personne_id }
      : null;

  const motLien = estMonParent
    ? personne.sexe === 'F'
      ? 'ma mère'
      : personne.sexe === 'M'
        ? 'mon père'
        : 'mon parent'
    : personne.sexe === 'F'
      ? 'ma fille'
      : personne.sexe === 'M'
        ? 'mon fils'
        : 'mon enfant';

  async function contester() {
    setErreur(null);
    setEnvoi(true);
    try {
      await api.contester(
        lienDirect!.enfant,
        lienDirect!.parent,
        motif.trim() || undefined,
      );
      onModifie();
      onFerme();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Contestation impossible.');
      setContestation(false);
    } finally {
      setEnvoi(false);
    }
  }

  async function enregistrer(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      await api.completerPersonne(personne.personne_id, identite);
      onModifie();
      onFerme();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Enregistrement impossible.');
    } finally {
      setEnvoi(false);
    }
  }

  async function supprimer() {
    setErreur(null);
    setEnvoi(true);
    try {
      await api.supprimerPersonne(personne.personne_id);
      onModifie();
      onFerme();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Suppression impossible.');
      setConfirmeSuppression(false);
    } finally {
      setEnvoi(false);
    }
  }

  const maj = (champ: keyof Identite, v: string) =>
    setIdentite({ ...identite, [champ]: v === '' ? undefined : v });

  return (
    <div
      className="voile"
      onClick={(e) => {
        if (e.target === e.currentTarget) onFerme();
      }}
    >
      <div className="feuille" role="dialog" aria-modal="true" aria-label={nomComplet(personne.nom, personne.prenoms)}>
        <div className="feuille-poignee" aria-hidden="true" />

        <div className="masque-entete">
          <Avatar
            id={personne.personne_id}
            sexe={personne.sexe}
            tranche={trancheDepuisGeneration(
              personne.profondeur_moi - personne.profondeur_autre,
            )}
            inconnu={sansNom}
            taille={54}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>
              {sansNom ? 'Nom inconnu' : nomComplet(personne.nom, personne.prenoms)}
              {personne.decede && <span className="doux"> · †</span>}
            </div>
            <div className="relation">
              {personne.libelle}
              {personne.cote && personne.profondeur_moi >= 2 && (
                <span className="doux" style={{ fontWeight: 400 }}>
                  {' '}
                  ({personne.cote === 'pere' ? 'côté paternel' : 'côté maternel'})
                </span>
              )}
            </div>
          </div>
        </div>

        {erreur && <Alerte>{erreur}</Alerte>}

        {sansNom && !edition && (
          <p className="doux">
            Cette personne relie votre famille sans être nommée. Yambi l'a
            créée pour porter le lien. Si vous connaissez son nom, ajoutez-le.
          </p>
        )}

        {!edition ? (
          <>
            {personne.village_origine && (
              <p>
                <span className="doux">Village d'origine : </span>
                {personne.village_origine}
              </p>
            )}
            {!personne.village_origine && !sansNom && (
              <p className="doux">Aucune autre information enregistrée.</p>
            )}

            <div className="rangee-boutons" style={{ marginTop: 14 }}>
              <button className="bouton secondaire" onClick={onFerme}>
                Fermer
              </button>
              <button className="bouton" onClick={() => setEdition(true)}>
                {sansNom ? 'Lui donner un nom' : 'Compléter'}
              </button>
            </div>

            {!sansNom && (
              <Recits
                personneId={personne.personne_id}
                personneNom={nomComplet(personne.nom, personne.prenoms)}
              />
            )}

            {!confirmeSuppression ? (
              <button
                className="bouton danger mince"
                style={{ marginTop: 16 }}
                onClick={() => setConfirmeSuppression(true)}
              >
                Retirer de mon arbre
              </button>
            ) : (
              <div className="carte" style={{ marginTop: 16, marginBottom: 0 }}>
                <p className="doux">
                  Retirer cette personne ? Ce n'est possible que si vous l'avez
                  ajoutée vous-même et qu'aucun autre membre de la famille ne
                  s'appuie dessus.
                </p>
                <div className="rangee-boutons">
                  <button
                    className="bouton secondaire"
                    onClick={() => setConfirmeSuppression(false)}
                    disabled={envoi}
                  >
                    Annuler
                  </button>
                  <button className="bouton danger" onClick={supprimer} disabled={envoi}>
                    {envoi ? 'Un instant…' : 'Retirer'}
                  </button>
                </div>
              </div>
            )}

            {lienDirect && !confirmeSuppression && (
              <>
                {!contestation ? (
                  <button
                    className="bouton danger mince"
                    style={{ marginTop: 10 }}
                    onClick={() => setContestation(true)}
                  >
                    Ce n'est pas {motLien}
                  </button>
                ) : (
                  <div className="carte" style={{ marginTop: 16, marginBottom: 0 }}>
                    <p>
                      <strong>Retirer ce lien ?</strong>
                    </p>
                    <p className="doux">
                      Vous seul pouvez dire qui sont vos parents et vos enfants :
                      le lien sera retiré tout de suite, et la personne qui
                      l'avait déclaré en sera informée.
                    </p>
                    <p className="doux" style={{ marginBottom: 13 }}>
                      Toute la parenté qui passait par ce lien disparaîtra de
                      votre arbre — oncles, cousins, neveux de cette branche.
                      Vous pourrez revenir en arrière depuis « Mon compte ».
                    </p>

                    <div className="champ">
                      <label htmlFor="motif-contestation">
                        Pourquoi ? (facultatif)
                      </label>
                      <textarea
                        id="motif-contestation"
                        rows={2}
                        value={motif}
                        maxLength={2000}
                        onChange={(e) => setMotif(e.target.value)}
                        placeholder="Confusion avec une autre branche, homonyme…"
                      />
                    </div>

                    <div className="rangee-boutons">
                      <button
                        className="bouton secondaire"
                        onClick={() => setContestation(false)}
                        disabled={envoi}
                      >
                        Annuler
                      </button>
                      <button
                        className="bouton danger"
                        onClick={contester}
                        disabled={envoi}
                      >
                        {envoi ? 'Un instant…' : 'Retirer ce lien'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <form onSubmit={enregistrer} noValidate>
            <p className="doux" style={{ marginBottom: 13 }}>
              Seuls les champs encore vides seront enregistrés : on ne remplace
              pas ce qu'un autre membre de la famille a déjà renseigné.
            </p>

            <div className="duo">
              <Champ id="f-prenoms" etiquette="Prénom(s)" icone={<IconePersonne />}>
                <input
                  id="f-prenoms"
                  value={identite.prenoms ?? ''}
                  onChange={(e) => maj('prenoms', e.target.value)}
                  placeholder={personne.prenoms ?? ''}
                  disabled={Boolean(personne.prenoms)}
                  autoComplete="off"
                />
              </Champ>
              <Champ id="f-nom" etiquette="Nom" icone={<IconePersonne />}>
                <input
                  id="f-nom"
                  value={identite.nom ?? ''}
                  onChange={(e) => maj('nom', e.target.value)}
                  placeholder={personne.nom ?? ''}
                  disabled={Boolean(personne.nom)}
                  autoComplete="off"
                />
              </Champ>
            </div>

            {!personne.sexe && (
              <fieldset>
                <legend>Sexe</legend>
                <div className="radios">
                  {(['M', 'F'] as const).map((s) => (
                    <label key={s}>
                      <input
                        type="radio"
                        name="f-sexe"
                        checked={identite.sexe === s}
                        onChange={() => maj('sexe', s)}
                      />
                      {s === 'M' ? 'Homme' : 'Femme'}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {!personne.village_origine && (
              <Champ id="f-village" etiquette="Village d'origine" icone={<IconeLieu />}>
                <input
                  id="f-village"
                  value={identite.village_origine ?? ''}
                  onChange={(e) => maj('village_origine', e.target.value)}
                  autoComplete="off"
                />
              </Champ>
            )}

            <div className="rangee-boutons">
              <button
                type="button"
                className="bouton secondaire"
                onClick={() => (sansNom ? onFerme() : setEdition(false))}
                disabled={envoi}
              >
                Annuler
              </button>
              <button
                className="bouton"
                type="submit"
                disabled={envoi || Object.keys(identite).length === 0}
              >
                {envoi ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
